import React from 'react';
import PropTypes from 'prop-types';
import { getServerAddr } from './editor/config/config';

const TOKEN_STORAGE_KEY = 'nextgen_page_invite_token_v1';
const DEFAULT_VERIFY_ROUTE = '/api/v1/SiteInvite/Verify';
const DEFAULT_CHECK_ROUTE = '/api/v1/SiteInvite/Check';

function isInviteRequired() {
  return String(process.env.REACT_APP_SITE_INVITE_REQUIRED || '').trim().toLowerCase() === 'true';
}

function buildServerUrl(route) {
  if (/^https?:\/\//i.test(route)) return route;
  const inviteServerAddr = process.env.REACT_APP_SERVER_ADDR || getServerAddr();
  return `${inviteServerAddr.replace(/\/+$/, '')}${route.startsWith('/') ? route : `/${route}`}`;
}

function getInviteToken() {
  try {
    return window.localStorage?.getItem(TOKEN_STORAGE_KEY) || '';
  } catch (_) {
    return '';
  }
}

function persistInviteToken(token) {
  try {
    if (token) window.localStorage?.setItem(TOKEN_STORAGE_KEY, token);
    else window.localStorage?.removeItem(TOKEN_STORAGE_KEY);
  } catch (_) {
    // Browsers may block storage in private or embedded contexts.
  }
}

async function requestInviteCheck() {
  const token = getInviteToken();
  if (!token) return false;

  const route = process.env.REACT_APP_SITE_INVITE_CHECK_ROUTE || DEFAULT_CHECK_ROUTE;
  const response = await fetch(buildServerUrl(route), {
    method: 'GET',
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    persistInviteToken('');
    return false;
  }

  const data = await response.json().catch(() => ({}));
  if (!data?.ok) persistInviteToken('');
  return Boolean(data?.ok);
}

async function requestInviteVerify(code) {
  const route = process.env.REACT_APP_SITE_INVITE_VERIFY_ROUTE || DEFAULT_VERIFY_ROUTE;
  const response = await fetch(buildServerUrl(route), {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.ok || !data?.token) {
    throw new Error(data?.message || '邀请码不正确，请确认后再试。');
  }

  persistInviteToken(data.token);
}

function InviteGate({ initialChecking, onUnlock }) {
  const [inviteCode, setInviteCode] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const submitInvite = async (event) => {
    event.preventDefault();
    const normalizedCode = inviteCode.trim();

    if (!normalizedCode) {
      setError('请输入邀请码。');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await requestInviteVerify(normalizedCode);
      onUnlock();
    } catch (err) {
      setError(err?.message || '验证失败，请稍后再试。');
    } finally {
      setSubmitting(false);
    }
  };

  if (initialChecking) {
    return (
      <main style={styles.page}>
        <div style={styles.panel}>
          <div style={styles.kicker}>NextGen</div>
          <h1 style={styles.title}>正在验证访问权限</h1>
        </div>
      </main>
    );
  }

  return (
    <main style={styles.page}>
      <form style={styles.panel} onSubmit={submitInvite}>
        <div style={styles.kicker}>NextGen</div>
        <h1 style={styles.title}>请输入邀请码</h1>
        <p style={styles.copy}>
          当前站点仅对受邀成员开放，验证通过后会继续加载页面。
        </p>
        <div style={styles.label} id="site-invite-code-label">
          邀请码
        </div>
        <input
          id="site-invite-code"
          aria-labelledby="site-invite-code-label"
          autoComplete="off"
          inputMode="text"
          value={inviteCode}
          disabled={submitting}
          onChange={(event) => {
            setInviteCode(event.target.value);
            if (error) setError('');
          }}
          style={styles.input}
        />
        {error ? <div style={styles.error}>{error}</div> : null}
        <button type="submit" style={styles.button} disabled={submitting}>
          {submitting ? '验证中' : '进入'}
        </button>
      </form>
    </main>
  );
}

InviteGate.propTypes = {
  initialChecking: PropTypes.bool,
  onUnlock: PropTypes.func.isRequired,
};

InviteGate.defaultProps = {
  initialChecking: false,
};

export function renderInviteGateIfNeeded(root, onUnlock) {
  if (typeof window === 'undefined') return false;

  if (!isInviteRequired()) return false;

  root.render(<InviteGate initialChecking onUnlock={onUnlock} />);
  requestInviteCheck()
    .then((ok) => {
      if (ok) {
        onUnlock();
      } else {
        root.render(<InviteGate onUnlock={onUnlock} />);
      }
    })
    .catch(() => {
      persistInviteToken('');
      root.render(<InviteGate onUnlock={onUnlock} />);
    });
  return true;
}

const styles = {
  page: {
    minHeight: '100dvh',
    display: 'grid',
    placeItems: 'center',
    padding: 24,
    background:
      'radial-gradient(circle at 50% 18%, rgba(34, 197, 94, 0.18), transparent 30%), #020409',
    color: '#f8fafc',
    boxSizing: 'border-box',
  },
  panel: {
    width: 'min(100%, 384px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 24,
    border: '1px solid rgba(148, 163, 184, 0.24)',
    borderRadius: 8,
    background: 'rgba(2, 6, 23, 0.82)',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.42)',
  },
  kicker: {
    color: '#4ade80',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0,
  },
  title: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.15,
    fontWeight: 800,
  },
  copy: {
    margin: '0 0 8px',
    color: '#cbd5e1',
    fontSize: 14,
    lineHeight: 1.6,
  },
  label: {
    color: '#e2e8f0',
    fontSize: 13,
    fontWeight: 700,
  },
  input: {
    width: '100%',
    height: 44,
    border: '1px solid rgba(148, 163, 184, 0.34)',
    borderRadius: 8,
    padding: '0 12px',
    color: '#f8fafc',
    background: 'rgba(15, 23, 42, 0.72)',
    outline: 'none',
    boxSizing: 'border-box',
  },
  error: {
    color: '#fca5a5',
    fontSize: 13,
    lineHeight: 1.4,
  },
  button: {
    height: 44,
    border: 0,
    borderRadius: 8,
    marginTop: 4,
    color: '#00160c',
    background: '#4ade80',
    fontWeight: 800,
    cursor: 'pointer',
  },
};
